import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
  ArrowLeft,
  Check,
  Coffee,
  Copy,
  Gift,
  PartyPopper,
  Send,
  Sparkles,
  Heart,
  Wallet2,
  CreditCard as CardIcon,
  X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api from "../../services";
import SquarePayment from "../../components/SquarePayment";
import ErrorHandlerPage from "../../components/ErrorHandlerPage";

// ── Designs ────────────────────────────────────────────────────────────────────
type DesignId = "cafe" | "sample" | "thankyou" | "congrats";

interface CardDesign {
  id: DesignId;
  name: string;
  tagline: string;
  defaultMessage: string;
  background: string;
  glow: string;
  Icon: React.ComponentType<{ className?: string }>;
}

const DESIGNS: CardDesign[] = [
  {
    id: "cafe",
    name: "Café",
    tagline: "Enjoy a treat on us",
    defaultMessage: "Enjoy something delicious, on me!",
    background: "linear-gradient(135deg,#0b3d2e 0%,#1e6b4f 55%,#2f9e73 100%)",
    glow: "rgba(120,255,190,0.35)",
    Icon: Coffee,
  },
  {
    id: "sample",
    name: "Sample",
    tagline: "A little something for you",
    defaultMessage: "A little something to brighten your day.",
    background: "linear-gradient(135deg,#111827 0%,#312e81 55%,#4f46e5 100%)",
    glow: "rgba(165,180,252,0.35)",
    Icon: Sparkles,
  },
  {
    id: "thankyou",
    name: "Thank You",
    tagline: "With heartfelt thanks",
    defaultMessage: "Thank you so much — this one's for you!",
    background: "linear-gradient(135deg,#9a3412 0%,#ea580c 55%,#fb923c 100%)",
    glow: "rgba(255,214,170,0.4)",
    Icon: Heart,
  },
  {
    id: "congrats",
    name: "Congrats",
    tagline: "Time to celebrate",
    defaultMessage: "Congratulations! You deserve a celebration.",
    background: "linear-gradient(135deg,#581c87 0%,#a21caf 55%,#ec4899 100%)",
    glow: "rgba(255,200,240,0.4)",
    Icon: PartyPopper,
  },
];

const PRESETS = [25, 50, 100, 200];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

type Mode = "create" | "redeem";
type PaymentType = "wallet" | "stripe";

// ── Card preview ───────────────────────────────────────────────────────────────
const GiftCardPreview: React.FC<{
  design: CardDesign;
  amount: number | null;
  currencySymbol: string;
  receiverName: string;
  senderName: string;
  message: string;
  code?: string;
}> = ({ design, amount, currencySymbol, receiverName, senderName, message, code }) => (
  <div
    className="relative w-full overflow-hidden rounded-3xl text-white shadow-xl transition-all duration-500"
    style={{ background: design.background, aspectRatio: "1.7 / 1" }}
  >
    <div
      className="pointer-events-none absolute -right-10 -top-10 h-56 w-56 rounded-full blur-2xl"
      style={{ background: design.glow }}
    />
    <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-white/10" />
    <div className="pointer-events-none absolute -bottom-6 right-16 h-24 w-24 rounded-full bg-white/10" />
    <design.Icon className="pointer-events-none absolute right-5 top-5 h-16 w-16 text-white/20 sm:h-24 sm:w-24" />

    <div className="relative z-10 flex h-full flex-col justify-between p-5 sm:p-7">
      <div>
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-white/80">
          <Gift className="h-3.5 w-3.5" />
          Gift Card
        </div>
        <div className="mt-2 text-3xl font-extrabold leading-none sm:text-5xl">
          {amount ? `${currencySymbol}${amount}` : `${currencySymbol}—`}
        </div>
      </div>

      <div className="min-w-0">
        <p className="mb-1 line-clamp-2 text-xs italic text-white/90 sm:text-sm">
          “{message.trim() || design.defaultMessage}”
        </p>
        <div className="flex items-end justify-between gap-3 text-[11px] sm:text-xs">
          <div className="min-w-0">
            <div className="truncate text-white/70">
              To <span className="font-semibold text-white">{receiverName.trim() || "Recipient"}</span>
            </div>
            <div className="truncate text-white/70">
              From <span className="font-semibold text-white">{senderName || "You"}</span>
            </div>
          </div>
          {code && (
            <div className="rounded-lg bg-black/25 px-2.5 py-1 font-mono text-[11px] tracking-wider backdrop-blur sm:text-sm">
              {code}
            </div>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────
export const GiftCards: React.FC = () => {
  const navigate = useNavigate();
  const { user, setUser, currencySymbol, adminSquareApplicationId, adminSquareLocationId } = useAuth();

  const [mode, setMode] = useState<Mode>("create");

  // create
  const [designId, setDesignId] = useState<DesignId>("cafe");
  const [amount, setAmount] = useState<number | null>(null);
  const [receiverName, setReceiverName] = useState("");
  const [receiverEmail, setReceiverEmail] = useState("");
  const [message, setMessage] = useState("");
  const [touched, setTouched] = useState({ name: false, email: false, amount: false });
  const [creating, setCreating] = useState(false);
  const [squareOpen, setSquareOpen] = useState(false);
  const [created, setCreated] = useState<{ code: string; receiverName: string; receiverEmail: string } | null>(null);
  const [copied, setCopied] = useState(false);

  // redeem
  const [code, setCode] = useState("");
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState<string | null>(null);
  const [redeemedOk, setRedeemedOk] = useState(false);

  const [fatalError, setFatalError] = useState<{ title: string; description: string } | null>(null);

  const design = useMemo(() => DESIGNS.find((d) => d.id === designId)!, [designId]);
  const walletBalance = user?.wallet?.balance ?? 0;

  const errors = {
    name: receiverName.trim() ? "" : "Enter the recipient's name",
    email: !receiverEmail.trim()
      ? "Enter the recipient's email"
      : EMAIL_RE.test(receiverEmail.trim())
        ? ""
        : "Enter a valid email address",
    amount: amount && amount > 0 ? "" : "Choose or enter an amount",
  };
  const formValid = !errors.name && !errors.email && !errors.amount;
  const insufficientWallet = !!amount && amount > walletBalance;

  const markAllTouched = () => setTouched({ name: true, email: true, amount: true });

  const handleAmountInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const cleaned = e.target.value.replace(/[^0-9.]/g, "");
    const n = parseFloat(cleaned);
    setAmount(cleaned && !isNaN(n) ? n : null);
    setTouched((t) => ({ ...t, amount: true }));
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 16);
    setCode(raw.match(/.{1,4}/g)?.join("-") ?? "");
    setRedeemError(null);
    setRedeemedOk(false);
  };

  const formatCode = (c: string) => c.replace(/-/g, "").match(/.{1,4}/g)?.join("-") ?? c;

  const copyCode = async (c: string) => {
    try {
      await navigator.clipboard.writeText(c);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  // Same endpoint as the wallet page; the extra fields carry the recipient
  // info + design so the backend can send the email.
  const createGiftCard = async (paymentType: PaymentType) => {
    if (!amount) return;
    try {
      setCreating(true);
      const res = await api.post(`/wallet/createGiftCard`, {
        amount,
        paymentType,
        receiverName: receiverName.trim(),
        receiverEmail: receiverEmail.trim().toLowerCase(),
        senderName: user?.name,
        senderEmail: user?.email,
        message: message.trim() || design.defaultMessage,
        design: design.id,
      });
      const data = res.data;

      if (typeof data.balance === "number") {
        setUser((prev) =>
          prev ? { ...prev, wallet: { ...prev.wallet, balance: data.balance } } : prev,
        );
      }
      setCreated({
        code: data.code,
        receiverName: receiverName.trim(),
        receiverEmail: receiverEmail.trim(),
      });
    } catch (err) {
      console.error("Gift card creation request failed:", err);
      setFatalError({
        title: "Unable to Create Gift Card",
        description: "We could not create a gift card at this time.",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleWalletPay = () => {
    markAllTouched();
    if (!formValid || insufficientWallet) return;
    createGiftCard("wallet");
  };

  const handleCardPay = () => {
    markAllTouched();
    if (!formValid) return;
    setSquareOpen(true);
  };

  const resetCreate = () => {
    setCreated(null);
    setAmount(null);
    setReceiverName("");
    setReceiverEmail("");
    setMessage("");
    setTouched({ name: false, email: false, amount: false });
  };

  const handleRedeem = async () => {
    if (!code) return;
    try {
      setRedeeming(true);
      setRedeemError(null);
      const res = await api.post(`/wallet/redeemGiftCard`, { code: code.replace(/-/g, "") });
      const data = res.data;
      if (data.wallet?.bonusWallet && typeof data.wallet.bonusWallet.balance === "number") {
        setUser((prev) =>
          prev
            ? {
                ...prev,
                bonusWallet: { ...prev.bonusWallet, balance: data.wallet.bonusWallet.balance },
              }
            : prev,
        );
      }
      setRedeemedOk(true);
      setCode("");
    } catch (err: any) {
      console.error("Gift card redeem request failed:", err);
      setRedeemError(
        err?.response?.data?.message || err?.response?.data?.error || "This code couldn't be redeemed. Check it and try again.",
      );
    } finally {
      setRedeeming(false);
    }
  };

  if (fatalError) {
    return (
      <ErrorHandlerPage
        title={fatalError.title}
        description={fatalError.description}
        retryLabel="Back to Gift Cards"
        onRetry={() => setFatalError(null)}
      />
    );
  }

  // ── Shared bits ──────────────────────────────────────────────────────────────
  const inputCls = (bad: boolean) =>
    `w-full rounded-xl border bg-white p-3 text-[15px] text-black outline-none transition focus:border-black ${
      bad ? "border-red-400" : "border-black/20"
    }`;
  const labelCls = "mb-1.5 block text-[13px] font-semibold text-black";
  const hintCls = "mt-1 text-xs text-red-500";

  const header = (
    <div className="mx-auto flex max-w-5xl items-center gap-3 px-4 py-4 sm:px-6">
      <button
        onClick={() => navigate(-1)}
        aria-label="Go back"
        className="flex h-[35px] w-[35px] flex-shrink-0 items-center justify-center rounded-full border border-black/20 bg-white text-black transition hover:bg-black/5"
      >
        <ArrowLeft className="h-4 w-4" />
      </button>
      <h2 className="m-0 text-xl font-bold leading-none text-black sm:text-2xl">Gift Cards</h2>
    </div>
  );

  // Not signed in
  if (!user) {
    const loadingUser = !!Cookies.get("Logintoken");
    return (
      <div className="min-h-screen bg-white">
        {header}
        <div className="mx-auto mt-16 max-w-sm px-6 text-center text-black">
          {loadingUser ? (
            <p className="text-sm text-black/60">Loading your account…</p>
          ) : (
            <>
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-black/5">
                <Gift className="h-7 w-7" />
              </div>
              <h3 className="text-lg font-bold">Sign in to use gift cards</h3>
              <p className="mt-1 text-sm text-black/60">
                Create gift cards for friends or redeem one you've received.
              </p>
              <button
                onClick={() => navigate("/")}
                className="dm-wallet-cta mt-5 w-full rounded-xl bg-main py-3 font-bold text-white"
              >
                Back to menu
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {header}

      <div className="mx-auto max-w-5xl px-4 pb-12 sm:px-6">
        {/* Mode switch */}
        <div className="relative mx-auto mb-6 flex w-full max-w-md rounded-full border border-black/20 bg-white p-1">
          <span
            className={`absolute bottom-1 top-1 rounded-full bg-black transition-all duration-300 ${
              mode === "create" ? "left-1 right-1/2" : "left-1/2 right-1"
            }`}
          />
          {(["create", "redeem"] as Mode[]).map((m) => (
            <button
              key={m}
              onClick={() => setMode(m)}
              className={`relative z-10 flex flex-1 items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors ${
                mode === m ? "!text-white" : "text-black"
              }`}
            >
              {m === "create" ? <Send className="h-4 w-4" /> : <Gift className="h-4 w-4" />}
              {m === "create" ? "Create" : "Redeem"}
            </button>
          ))}
        </div>

        {/* ───────────── CREATE ───────────── */}
        {mode === "create" && !created && (
          <div className="grid gap-6 lg:grid-cols-[1fr_1.05fr] lg:gap-8">
            {/* Left: designs + preview */}
            <div className="lg:sticky lg:top-4 lg:self-start">
              <GiftCardPreview
                design={design}
                amount={amount}
                currencySymbol={currencySymbol}
                receiverName={receiverName}
                senderName={user.name}
                message={message}
              />
              <p className="mb-2 mt-5 text-[13px] font-semibold text-black">Choose a design</p>
              <div className="grid grid-cols-4 gap-2 sm:gap-3">
                {DESIGNS.map((d) => {
                  const active = d.id === designId;
                  return (
                    <button
                      key={d.id}
                      onClick={() => setDesignId(d.id)}
                      aria-pressed={active}
                      className={`group relative overflow-hidden rounded-2xl border-2 p-0 text-left transition ${
                        active ? "border-black scale-[1.02]" : "border-transparent opacity-80 hover:opacity-100"
                      }`}
                    >
                      <div
                        className="flex aspect-[1.4/1] items-center justify-center text-white"
                        style={{ background: d.background }}
                      >
                        <d.Icon className="h-6 w-6 sm:h-7 sm:w-7" />
                      </div>
                      <div className="bg-white px-1.5 py-1.5 text-center text-[11px] font-semibold text-black sm:text-xs">
                        {d.name}
                      </div>
                      {active && (
                        <span className="absolute right-1.5 top-1.5 flex h-5 w-5 items-center justify-center rounded-full bg-white text-black shadow">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Right: form */}
            <div className="rounded-2xl border border-black/5 bg-white p-4 sm:p-6">
              {/* Amount */}
              <label className={labelCls}>Amount</label>
              <div className="mb-2 flex flex-wrap gap-2">
                {PRESETS.map((v) => (
                  <button
                    key={v}
                    onClick={() => {
                      setAmount(v);
                      setTouched((t) => ({ ...t, amount: true }));
                    }}
                    className={`rounded-xl border px-4 py-2 text-sm font-semibold transition ${
                      amount === v
                        ? "border-black bg-black !text-white"
                        : "border-black/20 bg-white text-black hover:border-black"
                    }`}
                  >
                    {currencySymbol}
                    {v}
                  </button>
                ))}
              </div>
              <input
                inputMode="decimal"
                value={amount === null ? "" : `${currencySymbol}${amount}`}
                onChange={handleAmountInput}
                placeholder={`Custom amount (${currencySymbol})`}
                className={inputCls(touched.amount && !!errors.amount)}
              />
              {touched.amount && errors.amount && <p className={hintCls}>{errors.amount}</p>}

              {/* Recipient */}
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="gc-name" className={labelCls}>
                    Recipient name
                  </label>
                  <input
                    id="gc-name"
                    value={receiverName}
                    onChange={(e) => setReceiverName(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                    placeholder="e.g. Alex"
                    autoComplete="off"
                    className={inputCls(touched.name && !!errors.name)}
                  />
                  {touched.name && errors.name && <p className={hintCls}>{errors.name}</p>}
                </div>
                <div>
                  <label htmlFor="gc-email" className={labelCls}>
                    Recipient email
                  </label>
                  <input
                    id="gc-email"
                    type="email"
                    value={receiverEmail}
                    onChange={(e) => setReceiverEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    placeholder="alex@email.com"
                    autoComplete="off"
                    className={inputCls(touched.email && !!errors.email)}
                  />
                  {touched.email && errors.email && <p className={hintCls}>{errors.email}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="gc-msg" className={labelCls}>
                  Message <span className="font-normal text-black/50">(optional)</span>
                </label>
                <textarea
                  id="gc-msg"
                  value={message}
                  onChange={(e) => setMessage(e.target.value.slice(0, 140))}
                  rows={3}
                  placeholder={design.defaultMessage}
                  className={`${inputCls(false)} resize-none`}
                />
                <div className="mt-1 text-right text-[11px] text-black/40">{message.length}/140</div>
              </div>

              {/* Pay */}
              <div className="mt-4 flex items-center gap-3 rounded-xl border border-black/10 px-4 py-3 text-sm text-black">
                <Wallet2 className="h-5 w-5 flex-shrink-0" />
                <span className="flex-1 text-black/70">Wallet balance</span>
                <span className="font-bold">
                  {currencySymbol}
                  {walletBalance.toFixed(2)}
                </span>
              </div>
              {insufficientWallet && (
                <p className="mt-2 text-xs text-red-500">
                  Amount exceeds your wallet balance — pay by card instead.
                </p>
              )}

              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <button
                  onClick={handleWalletPay}
                  disabled={creating || insufficientWallet}
                  className="dm-wallet-cta flex items-center justify-center gap-2 rounded-xl bg-main py-3 font-bold text-white transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                >
                  <Wallet2 className="h-4 w-4" />
                  {creating ? "Processing…" : "Pay with Wallet"}
                </button>
                <button
                  onClick={handleCardPay}
                  disabled={creating}
                  className="dm-wallet-cta flex items-center justify-center gap-2 rounded-xl bg-main py-3 font-bold text-white transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
                >
                  <CardIcon className="h-4 w-4" />
                  Pay with Card
                </button>
              </div>
              <p className="mt-3 text-center text-xs text-black/50">
                We'll email the gift card to the recipient once payment is complete.
              </p>
            </div>
          </div>
        )}

        {/* Created success */}
        {mode === "create" && created && (
          <div className="mx-auto max-w-xl text-center text-black">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-600">
              <Check className="h-7 w-7" />
            </div>
            <h3 className="text-xl font-bold">Gift card created!</h3>
            <p className="mb-5 mt-1 text-sm text-black/60">
              We'll send it to <span className="font-semibold text-black">{created.receiverName}</span> at{" "}
              <span className="font-semibold text-black">{created.receiverEmail}</span>.
            </p>
            <GiftCardPreview
              design={design}
              amount={amount}
              currencySymbol={currencySymbol}
              receiverName={created.receiverName}
              senderName={user.name}
              message={message}
              code={formatCode(created.code)}
            />
            <button
              onClick={() => copyCode(formatCode(created.code))}
              className="mt-5 inline-flex items-center gap-2 rounded-xl border border-black/20 px-4 py-2.5 text-sm font-semibold transition hover:border-black"
            >
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied" : `Copy code ${formatCode(created.code)}`}
            </button>
            <button
              onClick={resetCreate}
              className="dm-wallet-cta mt-3 block w-full rounded-xl bg-main py-3 font-bold text-white"
            >
              Send another gift card
            </button>
          </div>
        )}

        {/* ───────────── REDEEM ───────────── */}
        {mode === "redeem" && (
          <div className="mx-auto max-w-md rounded-2xl border border-black/5 bg-white p-5 text-black sm:p-6">
            <div className="mb-5 flex items-center gap-3 rounded-xl border border-black/10 px-4 py-3">
              <Wallet2 className="h-8 w-8 flex-shrink-0" />
              <div>
                <p className="mb-0 text-xs text-black/60">Your Bonus Wallet Balance</p>
                <p className="mb-0 text-xl font-extrabold">
                  {currencySymbol}
                  {(user.bonusWallet?.balance ?? 0).toFixed(2)}
                </p>
              </div>
            </div>

            <label htmlFor="gc-code" className={labelCls}>
              Enter your gift card code
            </label>
            <input
              id="gc-code"
              value={code}
              onChange={handleCodeChange}
              onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
              placeholder="XXXX-XXXX-XXXX-XXXX"
              maxLength={19}
              autoComplete="off"
              className={`${inputCls(!!redeemError)} text-center font-mono text-lg tracking-wider`}
            />
            {redeemError && (
              <p className="mt-2 flex items-center gap-1.5 text-xs text-red-500">
                <X className="h-3.5 w-3.5" />
                {redeemError}
              </p>
            )}
            {redeemedOk && (
              <p className="mt-2 flex items-center gap-1.5 text-sm font-semibold text-green-600">
                <Check className="h-4 w-4" />
                Gift card redeemed — your balance has been updated.
              </p>
            )}

            <button
              onClick={handleRedeem}
              disabled={redeeming || code.replace(/-/g, "").length < 4}
              className="dm-wallet-cta mt-5 w-full rounded-xl bg-main py-3 font-bold text-white transition hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:scale-100"
            >
              {redeeming ? "Redeeming…" : "Redeem"}
            </button>
          </div>
        )}
      </div>

      {/* Card payment overlay */}
      {squareOpen && amount && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="relative flex max-h-[90vh] w-full max-w-xl flex-col overflow-y-auto rounded-2xl bg-white">
            <div className="flex items-center justify-between border-b border-black/20 px-4 py-4 sm:px-8">
              <h2 className="m-0 text-[20px] font-bold text-black">Complete Payment</h2>
              <button
                onClick={() => setSquareOpen(false)}
                aria-label="Close"
                className="text-black transition hover:scale-110"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5">
              <SquarePayment
                pricing={amount}
                handleOrder={() => createGiftCard("stripe")}
                onSuccess={() => setSquareOpen(false)}
                applicationId={adminSquareApplicationId}
                locationId={adminSquareLocationId}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GiftCards;
